import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Home, Library } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const ContinueReadingModal = ({ 
  isOpen, 
  onClose, 
  continueListeningBooks, 
  hasLastSession,
  onResumeListening 
}) => {
  const navigate = useNavigate();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-3xl sm:w-full max-h-[90vh] overflow-y-auto bg-slate-900 text-slate-50 border-slate-800 p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="text-xl sm:text-2xl font-bold">
            Continue Listening
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3 mb-3 sm:mb-4">
            <p className="text-xs sm:text-sm text-slate-300">
              Jump back into your last session the moment you&apos;re ready.
            </p>
            <span className="hidden rounded-full bg-slate-800 px-3 py-1 text-xs text-slate-300 sm:inline-flex whitespace-nowrap">
              Session-aware playback
            </span>
          </div>

          <div>
            {hasLastSession && continueListeningBooks?.length > 0 ? (
              <div className="space-y-3">
                {continueListeningBooks.map((book, index) => (
                  <div 
                    key={book.manuscript_id}
                    className="flex flex-col gap-4 rounded-2xl bg-slate-950/60 p-4 sm:flex-row sm:items-center"
                  >
                    <div className="flex-1">
                      <p className="text-xs uppercase tracking-[0.16em] text-slate-400">
                        {index === 0 ? 'Last active book' : `Recently played #${index + 1}`}
                      </p>
                      <h3 className="mt-1 text-base font-semibold sm:text-lg">
                        {book.bookTitle}
                      </h3>
                      <p className="mt-1 text-xs text-slate-300">
                        Chapter {book.chapterLabel} • {book.progressPercent}% complete
                      </p>
                      {/* Progress bar */}
                      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-800">
                        <div
                          className="h-full rounded-full bg-indigo-500 transition-all"
                          style={{ width: `${book.progressPercent}%` }}
                        />
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        onResumeListening(book.manuscript_id);
                        onClose();
                      }}
                      className="w-full sm:w-auto mt-2 inline-flex items-center justify-center rounded-full bg-indigo-500 px-4 sm:px-5 py-2 text-xs sm:text-sm font-semibold text-white shadow-lg shadow-indigo-900/40 transition hover:bg-indigo-400 sm:mt-0"
                    >
                      Resume listening
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-2xl bg-slate-950/60 p-6 text-center">
                <p className="font-medium text-slate-100 text-lg mb-2">
                  No active sessions yet
                </p>
                <p className="text-sm text-slate-400 mb-6">
                  Once you start listening to a book, your progress will appear here so you can pick up instantly next time.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={() => {
                      onClose();
                      navigate('/reader/my-library');
                    }}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-indigo-500"
                  >
                    <Library className="w-4 h-4" />
                    View My Library
                  </button>
                  <button
                    onClick={() => {
                      onClose();
                      navigate('/home');
                    }}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-700 px-5 py-2.5 text-sm font-semibold text-slate-100 transition hover:bg-slate-600"
                  >
                    <Home className="w-4 h-4" />
                    Return Home
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContinueReadingModal;
